import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn, getPasswordResetRedirectUrl } from '../lib/utils.mjs';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { wixClient } from '../config/wix.mjs';

const buildLoginPath = (email) => {
  const params = new URLSearchParams();
  const trimmedEmail = (email || '').trim();

  if (trimmedEmail) {
    params.set('email', trimmedEmail);
  }

  const query = params.toString();
  return query ? `/login?${query}` : '/login';
};

export function ForgotPasswordPage({
  className = 'w-full',
  ...props
}) {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  const resetRequired = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('required') === '1';
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextEmail = (params.get('email') || '').trim();

    if (!nextEmail) {
      return;
    }

    setEmail((currentEmail) => (currentEmail === nextEmail ? currentEmail : nextEmail));
  }, [location.search]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setStatus({
        type: 'error',
        message: 'Enter the email address tied to your account.',
      });
      return;
    }

    try {
      setIsLoading(true);
      setStatus({ type: '', message: '' });

      await wixClient.auth.sendPasswordResetEmail(
        trimmedEmail,
        getPasswordResetRedirectUrl()
      );

      setStatus({
        type: 'success',
        message:
          'If an account exists for that email, we sent a reset link. After you save a new password, return here and sign in with it.',
      });
    } catch (error) {
      console.error('Password reset error:', error);
      setStatus({
        type: 'error',
        message: 'Failed to send reset email. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loginPath = buildLoginPath(email);

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Reset Password</CardTitle>
          <CardDescription>
            Enter your account email and we&apos;ll send a secure reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              {resetRequired && (
                <div className="rounded-md bg-blue-100 p-3 text-sm text-blue-800">
                  This account needs a password reset before it can sign in.
                </div>
              )}
              {status.message && (
                <div
                  className={cn('rounded-md p-3 text-sm', {
                    'bg-green-100 text-green-800': status.type === 'success',
                    'bg-red-100 text-red-800': status.type === 'error',
                    'bg-blue-100 text-blue-800': status.type === 'info',
                  })}
                >
                  {status.message}
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="forgot-password-email">Email</Label>
                <Input
                  id="forgot-password-email"
                  name="email"
                  type="email"
                  placeholder="m@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                The email link opens Wix&apos;s hosted password reset screen. After you save a new
                password, Wix will send you back here to log in again.
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Sending reset link...' : 'Send reset link'}
              </Button>
              <div className="text-center text-sm text-slate-600">
                <Link to={loginPath} className="font-medium text-blue-600 hover:text-blue-800">
                  Back to login
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
